
import React, { useState, useMemo } from 'react';
import PatientList from './PatientList';
import PatientForm from './PatientForm';
import PatientDetailsModal from './PatientDetailsModal';
import { useData } from '../../context/DataContext';
import { Patient } from '../../types';
import Button from '../shared/Button';
import { Plus, Search, ChevronDown, ChevronUp } from 'lucide-react';

const PatientTab: React.FC = () => {
  const { patients, plans } = useData();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const handleAddPatient = () => {
    setEditingPatient(null);
    setIsFormOpen(true);
  };

  const handleEditPatient = (patient: Patient) => {
    setEditingPatient(patient);
    setIsFormOpen(true);
  };

  const handleViewDetails = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsDetailsOpen(true);
  };

  // Filtra pacientes pelo termo de busca
  const searchedPatients = useMemo(() => {
    return patients.filter(patient =>
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.phone.includes(searchTerm)
    );
  }, [patients, searchTerm]);

  // Segmenta os pacientes filtrados em Ativos e Inativos
  const { activePatients, inactivePatients } = useMemo(() => {
    const active = searchedPatients.filter(patient => 
      plans.some(plan => plan.patientId === patient.id && plan.status === 'Ativo')
    );
    
    const inactive = searchedPatients.filter(patient => 
      !plans.some(plan => plan.patientId === patient.id && plan.status === 'Ativo')
    );

    return { activePatients: active, inactivePatients: inactive };
  }, [searchedPatients, plans]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="relative w-full sm:w-auto">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-slate-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar paciente por nome, email ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full bg-white text-slate-900 sm:w-96 pl-10 pr-3 py-2 border border-slate-300 rounded-md leading-5 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <Button onClick={handleAddPatient} icon={<Plus size={16}/>}>
          Novo Paciente
        </Button>
      </div>

      {/* Seção de Pacientes Ativos */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
          <h3 className="font-bold text-slate-700 uppercase tracking-widest text-xs">
            Pacientes com Planos Ativos ({activePatients.length})
          </h3>
        </div>
        
        <PatientList
          patients={activePatients}
          onEdit={handleEditPatient}
          onViewDetails={handleViewDetails}
          emptyMessage="Nenhum paciente com plano ativo encontrado."
        />
      </section>

      {/* Seção de Pacientes Inativos (Accordion) */}
      <section className="border-t border-slate-200 pt-8">
        <button 
          onClick={() => setShowInactive(!showInactive)}
          className="flex items-center justify-between w-full p-4 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors shadow-sm"
        >
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-slate-700 uppercase tracking-widest text-xs">
              Pacientes com Planos Inativos ou Sem Planos ({inactivePatients.length})
            </h3>
          </div>
          {showInactive ? <ChevronUp size={20} className="text-slate-500"/> : <ChevronDown size={20} className="text-slate-500"/>}
        </button>
        
        {showInactive && (
          <div className="mt-6 animate-in fade-in slide-in-from-top-4 duration-300">
            <PatientList
              patients={inactivePatients}
              onEdit={handleEditPatient}
              onViewDetails={handleViewDetails}
              emptyMessage="Nenhum paciente inativo encontrado."
            />
          </div>
        )}
      </section>

      {isFormOpen && (
        <PatientForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          patient={editingPatient}
        />
      )}

      {isDetailsOpen && selectedPatient && (
        <PatientDetailsModal
          isOpen={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
          patient={selectedPatient}
        />
      )}
    </div>
  );
};

export default PatientTab;
